import React from "react";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { siteConfig } from "../config/siteConfig";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Building2,
} from "lucide-react";

const Contact = () => {
  const { t } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://skwoolens.trivanahospitality.com/en/contact"
      }
    ]
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "email": "NIKHILBANTH783@GMAIL.COM",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 28.0438528,
        "longitude": 73.2807272
      }
    }
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Contact S.K. Woollens | B-26 Un Mandi, Bikaner, Rajasthan"
        description="Contact S.K. Woollens at B-26, Un Mandi, Antyodaya Nagar, Bikaner. Direct WhatsApp and phone lines with partners Sunil Banthia and Nikhil Banthia for B2B raw wool supply."
        keywords="contact S.K. Woollens, wool supply enquiry, Bikaner wool contact, B-26 Un Mandi Bikaner, Nikhil Banthia phone, Sunil Banthia phone"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/contact"
        schema={[breadcrumbSchema, contactSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Phone className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Direct Connect</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("contact.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("contact.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* 2. Contact Methods & Partner Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-wool-charcoal">
                    {siteConfig.partners[0].name}
                  </h3>
                  <div className="text-xs font-semibold text-wool-bronze">
                    {siteConfig.partners[0].role}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                  WhatsApp Active
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <a
                  href={siteConfig.partners[0].whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 rounded-xl text-emerald-800 font-semibold transition"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>WhatsApp: +91 {siteConfig.partners[0].phone}</span>
                </a>

                <a
                  href={`tel:${siteConfig.partners[0].phone}`}
                  className="flex items-center gap-2.5 p-3 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-xl text-wool-charcoal font-semibold transition"
                >
                  <Phone className="w-4 h-4 text-wool-bronze shrink-0" />
                  <span>Call: +91 {siteConfig.partners[0].phone}</span>
                </a>

                <a
                  href={`mailto:${siteConfig.partners[0].email}`}
                  className="flex items-center gap-2.5 p-3 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-xl text-wool-charcoal font-semibold transition"
                >
                  <Mail className="w-4 h-4 text-wool-bronze shrink-0" />
                  <span>Email: {siteConfig.partners[0].email}</span>
                </a>
              </div>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-wool-charcoal">
                    {siteConfig.partners[1].name}
                  </h3>
                  <div className="text-xs font-semibold text-wool-bronze">
                    {siteConfig.partners[1].role}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                  WhatsApp Active
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <a
                  href={siteConfig.partners[1].whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 rounded-xl text-emerald-800 font-semibold transition"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>WhatsApp: +91 {siteConfig.partners[1].phone}</span>
                </a>

                <a
                  href={`tel:${siteConfig.partners[1].phone}`}
                  className="flex items-center gap-2.5 p-3 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-xl text-wool-charcoal font-semibold transition"
                >
                  <Phone className="w-4 h-4 text-wool-bronze shrink-0" />
                  <span>Call: +91 {siteConfig.partners[1].phone}</span>
                </a>

                <a
                  href={`mailto:${siteConfig.partners[1].email}`}
                  className="flex items-center gap-2.5 p-3 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-xl text-wool-charcoal font-semibold transition"
                >
                  <Mail className="w-4 h-4 text-wool-bronze shrink-0" />
                  <span>Email: {siteConfig.partners[1].email}</span>
                </a>
              </div>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-wool-bronze text-xs font-bold uppercase tracking-wider">
                <Building2 className="w-4 h-4" />
                <span>Firm Headquarters</span>
              </div>
              <div className="text-base font-bold text-wool-charcoal">
                S.K. Woollens
              </div>
              <div className="text-xs text-wool-slate flex items-start gap-1.5">
                <MapPin className="w-4 h-4 text-wool-bronze shrink-0 mt-0.5" />
                <span>{siteConfig.company.addressLine}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-premium flex flex-col justify-between">
            <div className="space-y-2 mb-6">
              <div className="wool-badge">
                <MessageCircle className="w-3.5 h-3.5 text-wool-bronze" />
                <span>WhatsApp Direct Connect</span>
              </div>
              <h2 className="text-2xl font-bold text-wool-charcoal font-display">
                Connect directly with S.K. Woollens
              </h2>
              <p className="text-xs text-wool-muted">
                Use WhatsApp or phone for prompt business discussion — no form required.
              </p>
            </div>

            <div className="space-y-4">
              <a
                href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-2xl text-emerald-800 font-semibold transition"
              >
                <MessageCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>WhatsApp: +91 7014581250</span>
              </a>

              <a
                href="tel:+917014581250"
                className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition"
              >
                <Phone className="w-5 h-5 text-wool-bronze shrink-0" />
                <span>Call: +91 7014581250</span>
              </a>

              <a
                href={`mailto:${siteConfig.contacts.primaryEmail}`}
                className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition"
              >
                <Mail className="w-5 h-5 text-wool-bronze shrink-0" />
                <span>Email: {siteConfig.contacts.primaryEmail}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Visit Us / Google Maps Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-premium">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Map Card */}
            <div className="lg:col-span-7 rounded-2xl overflow-hidden border border-wool-border min-h-[300px] relative">
              <iframe
                title="S.K. Woollens Location Map"
                src={siteConfig.contacts.googleMapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "320px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Visit Details Card */}
            <div className="lg:col-span-5 flex flex-col justify-between p-2 space-y-6">
              <div className="space-y-4">
                <div className="wool-badge">
                  <MapPin className="w-3.5 h-3.5 text-wool-bronze" />
                  <span>Visit S.K. Woollens</span>
                </div>
                <h3 className="text-xl font-bold text-wool-charcoal font-display">
                  Visit Our Sourcing Office
                </h3>
                <div className="text-xs text-wool-muted bg-wool-bg border border-wool-border/70 p-4 rounded-xl leading-relaxed">
                  <span className="font-semibold text-wool-bronze block mb-1">
                    Maps Listing: Bulaki Chand Kesari Chand
                  </span>
                  S.K. Woollens carries a wool-industry legacy dating back to 1965. Our current business operates under the name S.K. Woollens, while our Google Maps business listing is currently associated with the legacy name Bulaki Chand Kesari Chand.
                </div>
                <div className="text-sm text-wool-charcoal space-y-1">
                  <div className="font-bold">S.K. Woollens</div>
                  <div>B-26, Un Mandi</div>
                  <div>Antyodaya Nagar</div>
                  <div>Bikaner, Rajasthan 334001</div>
                  <div>India</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href={siteConfig.contacts.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-xs flex-1 text-center"
                >
                  Get Directions
                </a>
                <a
                  href={siteConfig.contacts.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-xs flex-1 text-center"
                >
                  Open in Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
